import { unlinkedCompanyProfileNotice } from '@/lib/companyProfileNotice';
/**
 * Phase forms-templates-unified — AdminFormsTabPanel.
 *
 * Full forms manager for a submission: lists required/manual forms,
 * supports Load Required Forms, Generate, PDF preview, and Approve workflows.
 *
 * Keeps the Phase 7.2 cover-letter action bar so existing cover-letter
 * functionality is preserved. The new section appears below it.
 */
import { MovableDialog } from '@/components/shared/MovableDialog';
import { isAutoFillable, MANUAL_FILL_HINT } from '@/constants/fillCapability';
import { useState, useCallback, useEffect, type ReactElement } from 'react';
import {
  v2,
  ApiError,
  parseApiErrorBody,
  type Document,
  type FormTemplateOption,
  type RequirementPickerItem,
  type RequiredTemplatesCatalogEntry,
  type Submission,
  type SubmissionForm,
  type SignatoryRequirements,
  type CoverLetterLetterheadReport,
  type GenerateBindingIssue,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { describeLetterheadReport } from '@/lib/letterheadMode';
import { missingSignatoryProfileId, profileSignatoriesHref } from '@/lib/signatory';
import { LoadRequiredPicker } from './LoadRequiredPicker';
import { ExtractFromPdfButton } from './ExtractFromPdfButton';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { useUserPermissions } from '@/hooks/useUserPermissions';

interface Props {
  submission?: Submission | null;
  documents?: readonly Document[] | null;
  /**
   * phase-lock-admin-forms-actions — true when the sequence is locked
   * (submitted/completed). A locked sequence's compiled package is what was
   * submitted; generating or regenerating forms after lock would drift the
   * forms record from the stored package, so ALL form-mutation affordances are
   * suppressed (absent, not disabled — cd2259b's convention). Read affordances
   * (view/download, statuses, progress, Refresh) stay: the tab becomes a
   * record, not a workbench. The server enforces the same rule (SequenceLocked).
   */
  isLocked?: boolean;
}

/** phase-lock-admin-forms-actions — why the form actions are gone on a locked sequence. */
const FORMS_LOCKED_HINT =
  'This sequence is locked (submitted or completed). Forms are frozen — they match the submitted package and cannot be changed unless it is unlocked.';

const DEMO_FORM_FIELDS: Record<string, string> = {
  applicantName: '',
  drugName: '',
  indication: '',
  dosageForm: '',
  strength: '',
  routeOfAdministration: '',
  manufacturer: '',
  contactEmail: '',
  phoneNumber: '',
  applicationNumber: '',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  ready: 'Ready',
  approved: 'Approved',
  manual: 'Manual',
};

const STATUS_COLORS: Record<string, string> = {
  draft: '#6b7280',
  ready: '#2563eb',
  approved: '#059669',
  manual: '#7c3aed',
};

// ─── FormRow ─────────────────────────────────────────────────────────────────

function FormRow({
  form,
  submissionId,
  onUpdated,
  locked = false,
  templateOptions,
  profileMissing = false,
  profileUnlinkedNote = null,
  signatoryMissingProfileId = null,
}: {
  form: SubmissionForm;
  submissionId: string;
  onUpdated: () => void;
  /** phase-lock-admin-forms-actions — suppress every per-form mutation. */
  locked?: boolean;
  /** phase-forms-workflow-follow-up (Item B) — templates for the fallback dropdown. */
  templateOptions: FormTemplateOption[];
  /** phase-missing-profile-visibility — the submission's linked company
   * profile is missing/deleted; Generate would refuse (409), so the control
   * is disabled with the reason shown rather than left clickable-and-doomed. */
  profileMissing?: boolean;
  /** phase-missing-profile-visibility — no profile is linked at all; Generate
   * stays enabled (the fallback still works) but names which profile it will
   * actually use, so the choice is visible before generating. */
  profileUnlinkedNote?: string | null;
  /** phase-company-profile-rows-7-9 (Row 7) — set (to the profile id) when
   * this form signs with the profile's authorized signatory and the profile
   * has none: the row warns BEFORE Generate, with a link to add one. */
  signatoryMissingProfileId?: string | null;
}): ReactElement {
  // T-07 (D-27) — approving a form is a sign-off: the route checks
  // Submissions.sign, so the button follows it (write does not imply sign).
  const canSign = useUserPermissions().hasPermission('Submissions', 'sign');
  const [linking, setLinking] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Row 7 — when the server still refuses with MissingSignatory, link to the
  // profile it names.
  const [errorSignatoryProfileId, setErrorSignatoryProfileId] = useState<string | null>(null);
  // phase-fix-path-b-variable-resolution §2.5 — variables that generated with a
  // default or a visible {{placeholder}} rather than real data.
  const [defaulted, setDefaulted] = useState<string[]>([]);
  // phase-fix-path-c-flatten-guardrail — government-form fields that generated
  // blank and were left live (form stored unflattened).
  const [fieldsUnfilled, setFieldsUnfilled] = useState<string[]>([]);
  // Form Templates row 6 — bindings that produced nothing (broken / empty).
  const [bindingIssues, setBindingIssues] = useState<GenerateBindingIssue[]>([]);
  const [showPdf, setShowPdf] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [ctdCode, setCtdCode] = useState('');
  // phase-forms-and-documents-review-batch (item 3) — per-form delete on
  // UNLOCKED sequences, wired to the existing guarded DELETE route (cc63e14
  // already refuses it server-side when locked). Confirm before removing.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // phase-fix-signatory-and-submission-date — the cover letter's date is a value
  // the user confirms at generation, not a silent server-side "today". Template
  // forms show this date field, defaulted to today but VISIBLE and editable, so
  // whatever date the letter carries was chosen on purpose.
  const isTemplateForm = form.formType === 'template';
  const [letterDate, setLetterDate] = useState<string>(() =>
    new Date().toISOString().slice(0, 10),
  );

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    setErrorSignatoryProfileId(null);
    setDefaulted([]);
    setFieldsUnfilled([]);
    setBindingIssues([]);
    try {
      const result = await v2.submissionForms.generate(
        submissionId,
        form.id,
        isTemplateForm && letterDate ? { letterDate } : undefined,
      );
      setDefaulted(result.warnings?.variablesDefaulted ?? []);
      setBindingIssues(result.warnings?.bindingIssues ?? []);
      setFieldsUnfilled(
        (result.warnings?.fieldsUnfilled ?? []).map((f) =>
          // Form Templates row 5 — a broken mapping says so, not just its path.
          f.reason === 'mapping broken'
            ? `${f.autoeCTDFieldPath} (mapping broken — field “${f.pdfFieldName}” is no longer in the PDF)`
            : f.autoeCTDFieldPath,
        ),
      );
      onUpdated();
    } catch (err) {
      // phase-fix-path-b-variable-resolution §2.5 — a MissingRequiredVariables
      // 400 carries a plain-language `message`. `ApiError.message` is the raw
      // JSON body, so read the structured field first (the established pattern
      // — see AutoRenameAffordance) or the user reads JSON at us.
      setError(
        err instanceof ApiError
          ? (parseApiErrorBody(err)?.message ?? err.message)
          : 'Generation failed',
      );
      setErrorSignatoryProfileId(missingSignatoryProfileId(err));
    } finally {
      setGenerating(false);
    }
  }, [submissionId, form.id, onUpdated, isTemplateForm, letterDate]);

  const handleApprove = useCallback(async () => {
    setApproving(true);
    setError(null);
    try {
      await v2.submissionForms.approve(submissionId, form.id, ctdCode ? { ctdSectionCode: ctdCode } : undefined);
      setShowApproveModal(false);
      onUpdated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Approval failed');
    } finally {
      setApproving(false);
    }
  }, [submissionId, form.id, ctdCode, onUpdated]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    setError(null);
    try {
      await v2.submissionForms.delete(submissionId, form.id);
      setShowDeleteConfirm(false);
      onUpdated();
    } catch (err) {
      // A locked sequence 403s here (server guard, cc63e14) — surface the
      // readable refusal rather than the raw JSON body.
      setError(
        err instanceof ApiError ? (parseApiErrorBody(err)?.message ?? err.message) : 'Delete failed',
      );
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }, [submissionId, form.id, onUpdated]);

  // phase-forms-workflow-follow-up (Item B) — a template form with no linked
  // DocumentTemplate (never auto-linked, or loaded before this feature —
  // "healing") can't generate until one is chosen from the fallback dropdown.
  const needsTemplate = isTemplateForm && !form.templateId;
  const handleLinkTemplate = useCallback(
    async (templateId: string) => {
      if (!templateId) return;
      setLinking(true);
      setError(null);
      try {
        await v2.submissionForms.update(submissionId, form.id, { templateId });
        onUpdated();
      } catch (err) {
        setError(
          err instanceof ApiError ? (parseApiErrorBody(err)?.message ?? err.message) : 'Failed to link template',
        );
      } finally {
        setLinking(false);
      }
    },
    [submissionId, form.id, onUpdated],
  );

  // phase-lock-admin-forms-actions — no generate/regenerate (nor the letter-date
  // input, which is gated on canGenerate) on a locked sequence.
  const canGenerate = !locked && form.formType !== 'manual' && form.status !== 'approved';
  // phase-fix-admin-forms-generate-government-form-id — a government form
  // with no linked GovernmentForms row can only 400 on generate; disable
  // the button and explain instead of offering an action that fails.
  const missingGovLink = form.formType === 'government' && !form.governmentFormId;
  // phase-fix-unfillable-form-detection — XFA/encrypted government forms
  // cannot be auto-filled; Generate is disabled with the manual-workflow
  // hint (download → fill in Adobe Reader → upload the completed PDF).
  const notAutoFillable =
    form.formType === 'government' && !isAutoFillable(form.govFillCapability);
  // phase-lock-admin-forms-actions — approve is a status-change mutation; gone when locked.
  const canApprove =
    canSign && !locked && form.status !== 'approved' && (form.status === 'ready' || form.status === 'manual');
  // phase-generate-with-gaps — the LAST generation's gaps. Non-empty means the
  // document still has visible «GAP» placeholders, so Approve is offered but
  // DISABLED with the reason (the server also refuses, authoritatively). A clean
  // regeneration clears form.gaps and re-enables Approve.
  const gaps = form.gaps ?? [];
  const hasGaps = gaps.length > 0;
  const gapReason =
    `Cannot approve: ${gaps.length} ${gaps.length === 1 ? 'value is' : 'values are'} still ` +
    `${gaps.length === 1 ? 'a placeholder' : 'placeholders'}: ${gaps.map((g) => g.label).join(', ')}. ` +
    `Add the missing details to the submission or company profile, then regenerate.`;
  const pdfUrl = v2.submissionForms.pdfUrl(submissionId, form.id);

  return (
    <div
      data-testid={`form-row-${form.id}`}
      style={{
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: 8,
        padding: '12px 14px',
        background: 'var(--surface, #fff)',
        marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {/* Status badge */}
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: 12,
            background: `${STATUS_COLORS[form.status] ?? '#6b7280'}20`,
            color: STATUS_COLORS[form.status] ?? '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {STATUS_LABELS[form.status] ?? form.status}
        </span>

        {/* Display name */}
        <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>
          {form.displayName}
        </span>

        {/* Form type label */}
        <span style={{ fontSize: 11, color: '#9ca3af', textTransform: 'capitalize' }}>
          {form.formType}
        </span>

        {/* phase-form-template-versioning — which template version produced
            the current generated content (recorded at generation time; the
            promoted Document carries the same number). */}
        {form.templateVersionNumber != null && (
          <span
            data-testid={`form-template-version-${form.id}`}
            title="The template version this content was generated from. Recorded when you generate; the approved document keeps it."
            style={{ fontSize: 11, color: '#9ca3af' }}
          >
            from template v{form.templateVersionNumber}.0
          </span>
        )}

        {/* phase-generate-with-gaps — the last generation left required values
            as visible «GAP» placeholders. Persisted (form.gaps), so it survives
            a reload. This is what blocks Approve until a clean regeneration. */}
        {hasGaps && (
          <span
            data-testid={`form-gaps-${form.id}`}
            title={gapReason}
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 12,
              background: '#dc262620',
              color: '#dc2626',
            }}
          >
            Generated with {gaps.length} {gaps.length === 1 ? 'gap' : 'gaps'}
          </span>
        )}

        {/* phase-form-template-versioning — the template moved on since this
            content was generated. Amber and INFORMATIONAL only: it never
            blocks Approve, and nothing regenerates unless the user chooses.
            Locked (published/submitted) sequences never get this flag — the
            server suppresses it. */}
        {form.templateVersionStale && (
          <span
            data-testid={`form-template-stale-${form.id}`}
            title={form.templateVersionStaleReason ?? undefined}
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 12,
              background: '#b4530920',
              color: '#b45309',
            }}
          >
            Newer template available
          </span>
        )}

        {/* phase-fix-path-c-flatten-guardrail — the stored PDF still has live,
            blank boxes (it was not sealed). Persisted, so it shows on reload. */}
        {form.unfilledFieldCount > 0 && (
          <span
            data-testid={`form-incomplete-${form.id}`}
            title="This form was generated with blank fields left live (not flattened) so they can still be completed in a PDF reader."
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 12,
              background: '#b4530920',
              color: '#b45309',
            }}
          >
            Incomplete — {form.unfilledFieldCount}{' '}
            {form.unfilledFieldCount === 1 ? 'field' : 'fields'} blank
          </span>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
          {/* phase-fix-signatory-and-submission-date — visible, editable letter
              date (defaults to today) for template forms; sent as letterDate. */}
          {canGenerate && isTemplateForm && (
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                color: 'var(--text-muted, #6b7280)',
              }}
            >
              <span>Letter date</span>
              <input
                type="date"
                data-testid={`letter-date-${form.id}`}
                value={letterDate}
                onChange={(e) => setLetterDate(e.target.value)}
                style={{
                  fontSize: 11,
                  padding: '2px 4px',
                  border: '1px solid var(--border, #d1d5db)',
                  borderRadius: 4,
                }}
              />
            </label>
          )}
          {/* phase-forms-workflow-follow-up (Item B) — fallback template picker
              for a template form with no linked DocumentTemplate. Choosing one
              persists it (PATCH templateId) and enables Generate. Also "heals"
              rows loaded before this feature. */}
          {canGenerate && needsTemplate && (
            <select
              data-testid={`template-picker-${form.id}`}
              disabled={linking}
              defaultValue=""
              onChange={(e) => void handleLinkTemplate(e.target.value)}
              title="Choose a template to generate this document"
              style={{
                fontSize: 11,
                padding: '3px 6px',
                border: '1px solid var(--border, #d1d5db)',
                borderRadius: 4,
                maxWidth: 260,
              }}
            >
              <option value="" disabled>
                {templateOptions.length === 0 ? 'No templates for this region' : 'Choose a template…'}
              </option>
              {templateOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.templateType ? ` (${t.templateType})` : ''}
                  {t.region === null ? ' — all regions' : ''}
                </option>
              ))}
            </select>
          )}
          {canGenerate && (
            <button
              type="button"
              data-testid={`generate-btn-${form.id}`}
              onClick={() => void handleGenerate()}
              disabled={generating || missingGovLink || notAutoFillable || needsTemplate || profileMissing}
              title={
                profileMissing
                  ? 'Company profile missing; document generation is unavailable until it is restored or relinked.'
                  : needsTemplate
                    ? 'Choose a template to generate this document'
                    : notAutoFillable
                      ? MANUAL_FILL_HINT
                      : missingGovLink
                        ? 'No fillable form template linked yet — upload the government form PDF under Form Templates to enable generation.'
                        : undefined
              }
              style={{
                ...smallBtnStyle('var(--accent, #2563eb)', '#fff'),
                ...(missingGovLink || notAutoFillable || needsTemplate || profileMissing
                  ? { opacity: 0.5, cursor: 'not-allowed' }
                  : {}),
              }}
            >
              {generating ? '…' : form.status === 'ready' ? 'Regenerate' : 'Generate'}
            </button>
          )}
          {/* phase-missing-profile-visibility — case (b): no profile is linked
              at all. Generate stays enabled (the fallback still works), but
              names which profile it will use so the choice is visible. */}
          {canGenerate && !profileMissing && profileUnlinkedNote && (
            <span
              data-testid={`generate-profile-unlinked-note-${form.id}`}
              title={profileUnlinkedNote}
              style={{ fontSize: 10, color: 'var(--amber, #b45309)', whiteSpace: 'nowrap' }}
            >
              ⚠ {profileUnlinkedNote}
            </span>
          )}
          {/* phase-company-profile-rows-7-9 (Row 7) — warned BEFORE generating:
              this form is signed by the profile's authorized signatory and the
              profile has none, so the server will refuse (422). */}
          {canGenerate && !profileMissing && signatoryMissingProfileId && (
            <span
              data-testid={`generate-signatory-missing-${form.id}`}
              style={{ fontSize: 10, color: 'var(--red, #dc2626)', whiteSpace: 'nowrap' }}
            >
              ⚠ Needs an authorized signatory —{' '}
              <a href={profileSignatoriesHref(signatoryMissingProfileId)} style={{ color: 'inherit' }}>
                add one
              </a>
            </span>
          )}

          {(form.hasPdf || form.generatedContent) && (
            <button
              type="button"
              data-testid={`preview-btn-${form.id}`}
              onClick={() => setShowPdf((p) => !p)}
              style={smallBtnStyle('#fff', 'var(--accent, #2563eb)', true)}
            >
              {showPdf ? 'Hide PDF' : 'Preview PDF'}
            </button>
          )}

          {canApprove && (
            <button
              type="button"
              data-testid={`approve-btn-${form.id}`}
              onClick={() => setShowApproveModal(true)}
              disabled={hasGaps}
              title={hasGaps ? gapReason : undefined}
              style={{
                ...smallBtnStyle('#059669', '#fff'),
                ...(hasGaps ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
              }}
            >
              Approve
            </button>
          )}

          {form.status === 'approved' && (
            <span style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>
              ✓ Approved
            </span>
          )}

          {/* phase-forms-and-documents-review-batch (item 3) — remove a loaded
              form. UNLOCKED only: on a locked sequence every form mutation is
              absent (cc63e14) and the server refuses the DELETE too. */}
          {!locked && (
            <button
              type="button"
              data-testid={`delete-form-btn-${form.id}`}
              onClick={() => setShowDeleteConfirm(true)}
              title="Remove this form from the submission"
              style={{
                ...smallBtnStyle('#fff', 'var(--red, #dc2626)', true),
                borderColor: 'var(--red, #dc2626)',
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <ConfirmModal
          title="Remove form?"
          message={`Remove "${form.displayName}" from this submission? This deletes the loaded form (and any generated content); the requirement definition is unaffected.`}
          confirmLabel={deleting ? 'Removing…' : 'Remove form'}
          variant="destructive"
          isLoading={deleting}
          onConfirm={() => void handleDelete()}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {error && (
        <p style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }} role="alert">
          {error}
          {errorSignatoryProfileId && (
            <>
              {' '}
              <a
                href={profileSignatoriesHref(errorSignatoryProfileId)}
                data-testid={`generate-error-signatory-link-${form.id}`}
                style={{ color: 'inherit', fontWeight: 600 }}
              >
                Add an authorized signatory →
              </a>
            </>
          )}
        </p>
      )}

      {/* phase-generate-with-gaps — generation proceeded but N required values
          had no source and were rendered as visible «GAP» placeholders. This
          carries the guidance that used to live in the red "can't generate yet"
          message, reworded for the generated-with-gaps state. Approve is blocked
          (here and on the server) until a clean regeneration clears these. */}
      {hasGaps && (
        <p
          data-testid={`form-gaps-detail-${form.id}`}
          style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}
          role="status"
        >
          ⚠ Generated with {gaps.length} {gaps.length === 1 ? 'gap' : 'gaps'} — shown as «…»
          placeholders in the document: {gaps.map((g) => g.label).join(', ')}. Add the missing
          details to the submission or company profile (or fill them from the template screen),
          then regenerate. Approve is blocked until every gap is filled.
        </p>
      )}

      {/* Form Templates row 6 — a bound variable that produced nothing names
          its binding and why (broken source vs empty value). */}
      {bindingIssues.length > 0 && (
        <div
          data-testid={`form-binding-issues-${form.id}`}
          style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}
          role="status"
        >
          ⚠ {bindingIssues.length} bound variable{bindingIssues.length === 1 ? '' : 's'} produced no
          value — fix the binding under Form Templates → variables:
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {bindingIssues.map((b) => (
              <li key={b.variableName}>
                «{b.variableName}» — {b.status === 'broken' ? 'broken binding' : 'empty'}: {b.issue}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* phase-fix-path-b-variable-resolution §2.5 — generation succeeded, but
          some values had no source. Say which, rather than handing over a
          document with quiet gaps in it. */}
      {defaulted.length > 0 && (
        <p
          data-testid={`form-defaulted-${form.id}`}
          style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}
          role="status"
        >
          ⚠ Generated with {defaulted.length} unfilled{' '}
          {defaulted.length === 1 ? 'value' : 'values'}: {defaulted.join(', ')}. Check the
          draft before approving — these appear as a default or a visible placeholder.
        </p>
      )}

      {/* phase-fix-path-c-flatten-guardrail — government form generated with
          blank optional fields. It was stored UNFLATTENED so those boxes stay
          fillable; say which, and that the form isn't sealed yet. */}
      {fieldsUnfilled.length > 0 && (
        <p
          data-testid={`form-fields-unfilled-${form.id}`}
          style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}
          role="status"
        >
          ⚠ Generated with {fieldsUnfilled.length} blank{' '}
          {fieldsUnfilled.length === 1 ? 'field' : 'fields'}: {fieldsUnfilled.join(', ')}. The
          form was left unsealed so you can complete these in any PDF reader — regenerate once
          the data is filled in to finalise it.
        </p>
      )}

      {/* PDF preview iframe */}
      {showPdf && (
        <div style={{ marginTop: 10 }}>
          <iframe
            src={pdfUrl}
            title={`PDF preview: ${form.displayName}`}
            style={{
              width: '100%',
              height: 500,
              border: '1px solid var(--border, #e5e7eb)',
              borderRadius: 6,
            }}
          />
        </div>
      )}

      {/* Approve confirmation modal */}
      {showApproveModal && (
        <ApproveModal
          displayName={form.displayName}
          ctdCode={ctdCode}
          onCtdCodeChange={setCtdCode}
          onConfirm={() => void handleApprove()}
          onCancel={() => setShowApproveModal(false)}
          loading={approving}
          error={error}
        />
      )}
    </div>
  );
}

// ─── ApproveModal ─────────────────────────────────────────────────────────────

function ApproveModal({
  displayName,
  ctdCode,
  onCtdCodeChange,
  onConfirm,
  onCancel,
  loading,
  error,
}: {
  displayName: string;
  ctdCode: string;
  onCtdCodeChange: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
}): ReactElement {
  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="submission.approveForm"
      title={<>Approve: {displayName}</>}
      ariaLabel={`Approve ${displayName}`}
      onClose={loading ? undefined : onCancel}
      escDisabled={loading}
      width={440}
      zIndex={1000}
    >
      <>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Approving will promote this form to the submission's document list.
          Optionally specify a CTD section code, or leave blank to auto-derive
          from the form name.
        </p>
        <label htmlFor="ctd-code-input" style={{ fontSize: 13, fontWeight: 500 }}>
          CTD Section Code (optional)
        </label>
        <input
          id="ctd-code-input"
          type="text"
          placeholder="e.g. 1.2 (auto-derived if blank)"
          value={ctdCode}
          onChange={(e) => onCtdCodeChange(e.target.value)}
          style={{
            display: 'block',
            width: '100%',
            marginTop: 6,
            padding: '8px 10px',
            borderRadius: 6,
            border: '1px solid #d1d5db',
            fontSize: 14,
            boxSizing: 'border-box',
          }}
        />
        {error && (
          <p style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }} role="alert">
            {error}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={smallBtnStyle('#fff', '#374151', true)}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="confirm-approve-btn"
            onClick={onConfirm}
            disabled={loading}
            style={smallBtnStyle('#059669', '#fff')}
          >
            {loading ? 'Approving…' : 'Approve & Promote'}
          </button>
        </div>
      </>
    </MovableDialog>
  );
}

// ─── ProgressBar ─────────────────────────────────────────────────────────────

function ProgressBar({ forms }: { forms: SubmissionForm[] }): ReactElement {
  const total = forms.length;
  const approved = forms.filter((f) => f.status === 'approved').length;
  const pct = total === 0 ? 0 : Math.round((approved / total) * 100);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
        <span>Forms progress</span>
        <span data-testid="forms-progress-label">{approved} / {total} approved ({pct}%)</span>
      </div>
      <div
        style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}
        role="progressbar"
        aria-valuenow={approved}
        aria-valuemin={0}
        aria-valuemax={total}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: pct === 100 ? '#059669' : '#2563eb',
            borderRadius: 3,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AdminFormsTabPanel({ submission, documents, isLocked = false }: Props): ReactElement {
  // Legacy demo form state (PDF extraction)
  const [formValues, setFormValues] = useState<Record<string, string>>(DEMO_FORM_FIELDS);
  const [selectedDocId, setSelectedDocId] = useState<string>('');
  const [lastPopulated, setLastPopulated] = useState<number>(0);

  // Cover letter state (Phase 7.2)
  const [coverLetterGenerating, setCoverLetterGenerating] = useState(false);
  const [coverLetterError, setCoverLetterError] = useState<string | null>(null);
  const [coverLetterDocId, setCoverLetterDocId] = useState<string | null>(null);
  // phase-company-profile-rows-7-9 — Row 7: the profile named by a
  // MissingSignatory refusal; Row 9: the letterhead the last letter used.
  const [coverLetterErrorProfileId, setCoverLetterErrorProfileId] = useState<string | null>(null);
  const [coverLetterLetterhead, setCoverLetterLetterhead] =
    useState<CoverLetterLetterheadReport | null>(null);
  // phase-company-profile-rows-7-9 (Row 7) — the pre-generate signatory check.
  // null = not known (not loaded, or the profile link itself is broken — that
  // has its own notice); never read as "fine".
  const [signatoryReq, setSignatoryReq] = useState<SignatoryRequirements | null>(null);

  // Forms manager state
  const [forms, setForms] = useState<SubmissionForm[]>([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingRequired, setLoadingRequired] = useState(false);
  const [formsError, setFormsError] = useState<string | null>(null);
  // Phase form-templates-submission-requirements-tab: template requirements surfaced
  // from /api/v1/submission-requirements/:region/:type after "Load Required Forms".
  const [templateRequirements, setTemplateRequirements] = useState<RequiredTemplatesCatalogEntry[]>([]);
  // phase-forms-workflow-follow-up (Item B) — DocumentTemplates for the region,
  // fed to each template row's fallback dropdown.
  const [templateOptions, setTemplateOptions] = useState<FormTemplateOption[]>([]);
  // phase-forms-workflow-follow-up (Item A) — the Load Required picker.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerItems, setPickerItems] = useState<RequirementPickerItem[] | null>(null);

  const handlePopulate = useCallback((matches: Record<string, string>) => {
    setFormValues((prev) => ({ ...prev, ...matches }));
    setLastPopulated(Object.keys(matches).length);
  }, []);

  const handleChange = useCallback((key: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Phase 7.2 — determine if there's an existing cover letter document
  const coverLetterDoc = (documents ?? []).find((d) => d.CtdSectionCode === '1.2') ?? null;
  const hasCoverLetter = coverLetterDoc !== null;

  // phase-missing-profile-visibility — the same fact the submission header
  // and the PROFILE-LINK validation rule read (getCompanyProfileLinkStatus),
  // carried on `submission` since the entity-model-stage-1 phase's GET route
  // enrichment. Every generate control below reads from these two derived
  // values so they can never disagree with the header flag.
  const profileLinkStatus = submission?.CompanyProfileLinkStatus;
  const profileMissing = profileLinkStatus?.state === 'missing';
  const profileUnlinkedNote =
    profileLinkStatus?.state === 'unlinked'
      ? unlinkedCompanyProfileNotice(profileLinkStatus.profileCount)
      : null;

  const handleGenerateCoverLetter = useCallback(async () => {
    if (!submission?.Id) return;
    setCoverLetterGenerating(true);
    setCoverLetterError(null);
    setCoverLetterErrorProfileId(null);
    try {
      const result = await v2.coverLetter.generate(submission.Id);
      setCoverLetterDocId(result.documentId);
      setCoverLetterLetterhead(result.letterhead ?? null);
    } catch (err) {
      // phase-fix-path-d-cover-letter-fallbacks — the 400 for missing
      // required values carries a plain-language message; surface it via the
      // established parser instead of the raw JSON body.
      setCoverLetterError(formatApiError(err, 'Generation failed'));
      setCoverLetterErrorProfileId(missingSignatoryProfileId(err));
    } finally {
      setCoverLetterGenerating(false);
    }
  }, [submission?.Id]);

  const handleRepopulateCoverLetter = useCallback(async () => {
    const docId = coverLetterDoc?.Id ?? coverLetterDocId;
    if (!docId) return;
    setCoverLetterGenerating(true);
    setCoverLetterError(null);
    setCoverLetterErrorProfileId(null);
    try {
      const result = await v2.documents.repopulateCoverLetter(docId);
      setCoverLetterDocId(docId);
      setCoverLetterLetterhead(result.letterhead ?? null);
    } catch (err) {
      setCoverLetterError(formatApiError(err, 'Regeneration failed'));
      setCoverLetterErrorProfileId(missingSignatoryProfileId(err));
    } finally {
      setCoverLetterGenerating(false);
    }
  }, [coverLetterDoc?.Id, coverLetterDocId]);

  // phase-company-profile-rows-7-9 (Row 7) — what needs a signatory here and
  // whether the profile has one, re-read whenever the forms list reloads (a
  // newly loaded form may sign; a signatory may have just been added).
  const loadSignatoryRequirements = useCallback(async () => {
    if (!submission?.Id) return;
    try {
      setSignatoryReq(await v2.submissionForms.signatoryRequirements(submission.Id));
    } catch {
      setSignatoryReq(null);
    }
  }, [submission?.Id]);
  const signatoryMissing = signatoryReq !== null && signatoryReq.signatory === null;
  const formsNeedingSignatory = new Set(
    (signatoryReq?.forms ?? []).filter((f) => f.requiresSignatory).map((f) => f.formId),
  );
  const signatoryNeedsSummary = (() => {
    const formCount = formsNeedingSignatory.size;
    const formPart = formCount === 1 ? '1 form' : `${formCount} forms`;
    if (signatoryReq?.coverLetterRequiresSignatory && formCount > 0) return `the cover letter and ${formPart}`;
    if (signatoryReq?.coverLetterRequiresSignatory) return 'the cover letter';
    return formPart;
  })();

  // ── Forms manager ──────────────────────────────────────────────────────────

  const loadForms = useCallback(async () => {
    if (!submission?.Id) return;
    setLoadingForms(true);
    setFormsError(null);
    try {
      // Phase phase-fix-admin-forms-tab-crash — `list()` resolves to the forms
      // array itself (apiFetch unwraps the { ok, data } envelope). This used to
      // read `res.data`, which was always undefined at runtime and crashed the
      // tab on `forms.length`. The Array.isArray guard keeps `forms` an array
      // even if the endpoint ever answers with an unexpected shape.
      const res = await v2.submissionForms.list(submission.Id);
      setForms(Array.isArray(res) ? res : []);
      void loadSignatoryRequirements();
      // Item B — the fallback-dropdown option set for this submission's region.
      try {
        const opts = await v2.submissionForms.templateOptions(submission.Id);
        setTemplateOptions(Array.isArray(opts) ? opts : []);
      } catch {
        setTemplateOptions([]);
      }
    } catch (err) {
      setForms([]);
      setFormsError(err instanceof ApiError ? err.message : 'Failed to load forms');
    } finally {
      setLoadingForms(false);
    }
  }, [submission?.Id, loadSignatoryRequirements]);

  useEffect(() => {
    void loadForms();
  }, [loadForms]);

  // phase-forms-workflow-follow-up (Item A) — open the picker (fetch the loadable
  // requirements) instead of dumping every required form.
  const handleLoadRequired = useCallback(async () => {
    if (!submission?.Id) return;
    setLoadingRequired(true);
    setFormsError(null);
    try {
      const items = await v2.submissionForms.requirementsPicker(submission.Id);
      setPickerItems(Array.isArray(items) ? items : []);
      setPickerOpen(true);
    } catch (err) {
      setFormsError(err instanceof ApiError ? err.message : 'Failed to load requirements');
    } finally {
      setLoadingRequired(false);
    }
  }, [submission?.Id]);

  // Item A — create only the chosen requirements, then refresh the panel.
  const handlePickerConfirm = useCallback(
    async (selection: string[]) => {
      if (!submission?.Id) return;
      const res = await v2.submissionForms.loadRequired(submission.Id, selection);
      setForms(Array.isArray(res) ? res : []);
      setPickerOpen(false);
      if (submission.Region && submission.SubmissionType) {
        try {
          const reqRes = await v2.submissionRequirements.getRequirements(submission.Region, submission.SubmissionType);
          setTemplateRequirements(Array.isArray(reqRes?.templates) ? reqRes.templates : []);
        } catch {
          /* non-fatal — the details section just stays empty */
        }
      }
      await loadForms();
    },
    [submission?.Id, submission?.Region, submission?.SubmissionType, loadForms],
  );

  const availableDocs = (documents ?? []).filter((d) =>
    d.FileName?.toLowerCase().endsWith('.pdf'),
  );

  return (
    <div style={{ padding: 16 }}>
      {/* phase-lock-admin-forms-actions — read-only notice on a locked
          sequence, matching the Documents tab's pattern. The tab stays
          informative (statuses, progress, view/download) but every
          form-mutation affordance is absent. */}
      {isLocked && (
        <div
          data-testid="forms-locked-note"
          title={FORMS_LOCKED_HINT}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16,
            padding: '10px 14px',
            background: 'var(--surface2, #f8fafc)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: 8,
            fontSize: 12,
            color: 'var(--text-muted, #6b7280)',
            fontWeight: 600,
          }}
        >
          🔒 This sequence is locked — forms are read-only.
        </div>
      )}

      {/* ── Phase 7.2 — Cover letter action bar ────────────────────────── */}
      {submission?.Id && (
        <div
          data-testid="cover-letter-action-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            padding: '12px 14px',
            background: 'var(--surface, #fff)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: 8,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 13 }}>Cover Letter</span>
          {/* phase-lock-admin-forms-actions — generate/regenerate are
              suppressed (absent) on a locked sequence; the status read below
              stays. */}
          {!isLocked &&
            (hasCoverLetter ? (
              <button
                type="button"
                data-testid="regenerate-cover-letter-btn"
                onClick={() => void handleRepopulateCoverLetter()}
                disabled={coverLetterGenerating || profileMissing}
                title={
                  profileMissing
                    ? 'Company profile missing; document generation is unavailable until it is restored or relinked.'
                    : undefined
                }
                style={{
                  ...smallBtnStyle('#fff', 'var(--accent, #2563eb)', true),
                  ...(profileMissing ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                }}
              >
                {coverLetterGenerating ? 'Regenerating…' : 'Regenerate cover letter'}
              </button>
            ) : (
              <button
                type="button"
                data-testid="generate-cover-letter-btn"
                onClick={() => void handleGenerateCoverLetter()}
                disabled={coverLetterGenerating || profileMissing}
                title={
                  profileMissing
                    ? 'Company profile missing; document generation is unavailable until it is restored or relinked.'
                    : undefined
                }
                style={{
                  ...smallBtnStyle('var(--accent, #2563eb)', '#fff'),
                  ...(profileMissing ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                }}
              >
                {coverLetterGenerating ? 'Generating…' : 'Generate cover letter'}
              </button>
            ))}
          {/* phase-missing-profile-visibility — case (a): the control itself
              carries the reason (disabled above); this makes it readable
              without hovering the button too. */}
          {profileMissing && (
            <span
              data-testid="cover-letter-profile-missing"
              role="alert"
              style={{ color: 'var(--red, #dc2626)', fontSize: 12 }}
            >
              ⚠ Company profile missing — restore it or relink the submission to generate.
            </span>
          )}
          {!profileMissing && profileUnlinkedNote && (
            <span
              data-testid="cover-letter-profile-unlinked-note"
              title={profileUnlinkedNote}
              style={{ fontSize: 11, color: 'var(--amber, #b45309)' }}
            >
              ⚠ {profileUnlinkedNote}
            </span>
          )}
          {coverLetterError && (
            <span style={{ color: 'var(--red, #dc2626)', fontSize: 12 }} role="alert">
              {coverLetterError}
              {coverLetterErrorProfileId && (
                <>
                  {' '}
                  <a
                    href={profileSignatoriesHref(coverLetterErrorProfileId)}
                    data-testid="cover-letter-error-signatory-link"
                    style={{ color: 'inherit', fontWeight: 600 }}
                  >
                    Add an authorized signatory →
                  </a>
                </>
              )}
            </span>
          )}
          {(coverLetterDocId ?? coverLetterDoc?.Id) && !coverLetterGenerating && (
            <span style={{ fontSize: 12, color: '#059669' }} data-testid="cover-letter-success">
              ✓ Cover letter generated (section 1.2)
            </span>
          )}
          {/* phase-company-profile-rows-7-9 (Row 9) — which profile letterhead
              the letter used (or why none), as the server reported it. */}
          {coverLetterLetterhead && !coverLetterGenerating && (
            <span
              data-testid="cover-letter-letterhead-report"
              style={{ fontSize: 11, color: coverLetterLetterhead.applied ? '#059669' : 'var(--amber, #b45309)' }}
            >
              {describeLetterheadReport(coverLetterLetterhead)}
            </span>
          )}
        </div>
      )}

      {/* phase-company-profile-rows-7-9 (Row 7) — BEFORE anything is
          generated: the profile has no authorized signatory, and the cover
          letter and/or forms here sign with one. Generating them is refused
          (422) until one is added — say so now, with the way to fix it. */}
      {submission?.Id && !isLocked && !profileMissing && signatoryMissing && signatoryReq?.companyProfileId && (
        <div
          role="alert"
          data-testid="signatory-missing-banner"
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            border: '1px solid var(--red, #dc2626)',
            background: 'var(--red-bg, #fef2f2)',
            borderRadius: 8,
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          <strong>No authorized signatory.</strong> Company profile “{signatoryReq.profileName}” has
          no authorized signatory{signatoryReq.region ? ` for ${signatoryReq.region} submissions` : ''},
          so {signatoryNeedsSummary} cannot be signed and will not be generated until one is added.{' '}
          <a
            href={profileSignatoriesHref(signatoryReq.companyProfileId)}
            data-testid="signatory-missing-link"
            style={{ fontWeight: 600 }}
          >
            Add an authorized signatory →
          </a>
        </div>
      )}

      {/* ── Forms Manager ──────────────────────────────────────────────── */}
      {submission?.Id && (
        <section data-testid="forms-manager" style={{ marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Submission Forms</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              {/* phase-lock-admin-forms-actions — "Load Required Forms" is a
                  MUTATION (it creates SubmissionForm rows); suppressed on a
                  locked sequence. "Refresh" is a READ (it re-fetches via GET
                  /forms, no write) — kept so the record stays refreshable. */}
              {!isLocked && (
                <button
                  type="button"
                  data-testid="load-required-forms-btn"
                  onClick={() => void handleLoadRequired()}
                  disabled={loadingRequired}
                  style={smallBtnStyle('var(--accent, #2563eb)', '#fff')}
                >
                  {loadingRequired ? 'Loading…' : 'Load Required Forms'}
                </button>
              )}
              <button
                type="button"
                data-testid="refresh-forms-btn"
                onClick={() => void loadForms()}
                disabled={loadingForms}
                style={smallBtnStyle('#fff', '#374151', true)}
              >
                ↺ Refresh
              </button>
            </div>
          </div>

          {formsError && (
            <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }} role="alert">
              {formsError}
            </p>
          )}

          {forms.length > 0 && <ProgressBar forms={forms} />}

          {loadingForms && forms.length === 0 && (
            <p style={{ fontSize: 13, color: '#6b7280' }}>Loading forms…</p>
          )}

          {!loadingForms && forms.length === 0 && (
            <div
              style={{
                padding: 20,
                textAlign: 'center',
                border: '1px dashed var(--border, #e5e7eb)',
                borderRadius: 8,
                color: '#6b7280',
                fontSize: 13,
              }}
            >
              {/* phase-lock-admin-forms-actions — no "Load required forms"
                  affordance on a locked sequence (it's a mutation). */}
              {isLocked ? (
                <>No forms were recorded for this locked sequence.</>
              ) : (
                <>
                  No forms yet. Use Load Required Forms above to get started.
                </>
              )}
            </div>
          )}

          {forms.map((form) => (
            <FormRow
              key={form.id}
              form={form}
              submissionId={submission.Id}
              onUpdated={() => void loadForms()}
              locked={isLocked}
              templateOptions={templateOptions}
              profileMissing={profileMissing}
              profileUnlinkedNote={profileUnlinkedNote}
              signatoryMissingProfileId={
                signatoryMissing && formsNeedingSignatory.has(form.id)
                  ? (signatoryReq?.companyProfileId ?? null)
                  : null
              }
            />
          ))}

          {/* phase-forms-workflow-follow-up (Item A) — Load Required picker. */}
          {pickerOpen && pickerItems && (
            <LoadRequiredPicker
              items={pickerItems}
              onConfirm={handlePickerConfirm}
              onCancel={() => setPickerOpen(false)}
            />
          )}

          {/* Phase form-templates-submission-requirements-tab — template requirements */}
          {templateRequirements.length > 0 && (
            <details
              data-testid="template-requirements-section"
              style={{ marginTop: 16 }}
            >
              <summary style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--text-muted)' }}>
                📄 Required Templates ({templateRequirements.length})
              </summary>
              <ul style={{ fontSize: 12, marginTop: 8, paddingLeft: 16 }}>
                {templateRequirements.map((t) => (
                  <li key={t.id} style={{ marginBottom: 4 }}>
                    <strong>{t.templateType}</strong> — {t.templateName}
                    {' '}
                    {t.isRequired
                      ? <span style={{ color: '#2e7d32' }}>✅ Required</span>
                      : t.conditional
                        ? <span style={{ color: '#b45309' }} title={t.conditional ?? ''}>🟡 Conditional</span>
                        : <span style={{ color: '#6b7280' }}>⚪ Optional</span>
                    }
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}

      {/* ── PDF extraction demo (legacy) ───────────────────────────────── */}
      <details>
        <summary
          style={{ fontSize: 13, fontWeight: 500, cursor: 'pointer', color: '#6b7280', marginBottom: 12 }}
        >
          PDF field extraction (demo)
        </summary>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <label htmlFor="doc-select" style={{ fontSize: 14, fontWeight: 500 }}>
            Source PDF:
          </label>
          <select
            id="doc-select"
            value={selectedDocId}
            onChange={(e) => setSelectedDocId(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #d1d5db',
              fontSize: 14,
              minWidth: 240,
            }}
          >
            <option value="">— Select a PDF document —</option>
            {availableDocs.map((d) => (
              <option key={d.Id} value={d.Id}>
                {d.OriginalFileName || d.FileName}
              </option>
            ))}
          </select>

          {selectedDocId && (
            <ExtractFromPdfButton
              documentId={selectedDocId}
              formKeys={Object.keys(DEMO_FORM_FIELDS)}
              onPopulate={handlePopulate}
            />
          )}

          {lastPopulated > 0 && (
            <span style={{ fontSize: 13, color: '#059669' }}>
              {lastPopulated} field{lastPopulated === 1 ? '' : 's'} auto-populated
            </span>
          )}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
          }}
        >
          {Object.entries(DEMO_FORM_FIELDS).map(([key]) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label
                htmlFor={key}
                style={{ fontSize: 13, fontWeight: 500, textTransform: 'capitalize' }}
              >
                {key.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, (c) => c.toUpperCase())}
              </label>
              <input
                id={key}
                type="text"
                value={formValues[key] ?? ''}
                onChange={(e) => handleChange(key, e.target.value)}
                style={{
                  padding: '8px 10px',
                  borderRadius: 6,
                  border: '1px solid #d1d5db',
                  fontSize: 14,
                }}
              />
            </div>
          ))}
        </div>

        {availableDocs.length === 0 && (
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 12 }}>
            No PDF documents available in this submission. Upload a PDF to the Documents tab first.
          </p>
        )}
      </details>
    </div>
  );
}

// ─── Shared button style helper ───────────────────────────────────────────────

function smallBtnStyle(bg: string, color: string, outlined = false): import('react').CSSProperties {
  return {
    padding: '6px 12px',
    background: bg,
    color,
    border: outlined ? `1px solid ${color}` : 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
}
